// ---------------------------------------------------------------------------
// The pieces a round is drawn out of: the frames, the tile row, and the numbers
// working.
//
// These lived inside `RevealScreen.tsx` until the game record page needed the
// same board, the same word, the same working and the same ruling, out of stored
// rows rather than live state. They were already pure — props in, markup out,
// no `RevealView`, no `ClientGameState`, no state of their own — so this is a
// move rather than a rewrite.
//
// Nothing here reads live game state, and nothing here should learn to. That is
// the whole reason the record page can use them: it has a finished game, not a
// room. Anything needing `state.hostId`, a clock, or whose turn it is belongs in
// the screen, not in this file.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import type { NumbersOperand, NumbersOperator, NumbersStep } from '@countdown/shared';

import { LetterTile, tileFitStyle } from '../primitives/LetterTile.js';
import type { LetterTileSize } from '../primitives/LetterTile.js';

const OPERATOR_TEXT: Record<NumbersOperator, string> = { '+': '+', '-': '-', '*': 'x', '/': '/' };

// --- Frames ----------------------------------------------------------------

export function Frame({
  children,
  className = '',
  dense = false,
}: {
  children: ReactNode;
  className?: string;
  dense?: boolean;
}) {
  const box = dense ? 'gap-2 p-3 sm:gap-3.5 sm:p-5' : 'gap-3 p-4 sm:gap-4 sm:p-5';
  return (
    // `relative` so an overlay inside a frame — the AI thinking panel — pins to
    // the frame rather than escaping to the nearest positioned ancestor, which
    // on the reveal screen is the page.
    <section
      className={`relative flex flex-col rounded-frame border border-line bg-bg ${box} ${className}`}
    >
      {children}
    </section>
  );
}

export function BlueFrame({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`flex flex-col gap-3 rounded-frame border border-blue-400/45 bg-blue-700/25 p-4 sm:gap-4 sm:p-5 ${className}`}
    >
      {children}
    </section>
  );
}

// --- The board, and a word out of it ---------------------------------------

export function TileRow({
  letters,
  tone,
  size = 'phone',
}: {
  letters: string[];
  tone: 'board' | 'answer' | 'rejected' | 'best';
  size?: LetterTileSize;
}) {
  // LetterTile has no "brighter blue" or "greyed out but not locked" state, so
  // the two reveal treatments are filters over the available face rather than
  // hand-written colours.
  //
  // A rejected row is desaturated and nothing else. It used to carry an opacity
  // as well, which faded the one word on the screen that the player most wants
  // to read. Marking an answer must never cost legibility of the answer.
  const wrapper =
    tone === 'rejected' ? 'saturate-[0.35]' : tone === 'best' ? 'brightness-110' : '';

  // It used to wrap, and wrapping was never the right answer to a row that is
  // too wide. Nine `board` tiles are 788px and the stage is 762 at every
  // desktop width, so a nine-letter word — the best word of a good round, and
  // *every* correct conundrum answer — came out on two lines. The row now
  // scales to whatever it has: `.tile-fit` is the query container and
  // `tileFitStyle` gives each tile the smaller of its designed width and its
  // share of the row.
  return (
    <div className="tile-fit w-full">
      <div
        className={`flex justify-center gap-1 ${wrapper}`}
        style={tileFitStyle(size, letters.length)}
      >
        {letters.map((letter, index) => (
          <LetterTile
            key={`${letter}-${index}`}
            letter={letter}
            state={tone === 'answer' ? 'highlight' : 'available'}
            size={size}
            fit
          />
        ))}
      </div>
    </div>
  );
}

// --- Numbers working -------------------------------------------------------

function operandValue(
  operand: NumbersOperand,
  tiles: number[],
  steps: NumbersStep[],
): number | null {
  if (operand.kind === 'tile') return tiles[operand.tileIndex] ?? null;
  const step = steps.find((candidate) => candidate.id === operand.stepId);
  return step === undefined ? null : step.result;
}

export function stepText(step: NumbersStep, tiles: number[], steps: NumbersStep[]): string {
  const left = operandValue(step.left, tiles, steps);
  const right = operandValue(step.right, tiles, steps);
  return `${left ?? '?'} ${OPERATOR_TEXT[step.op]} ${right ?? '?'}`;
}

export function StepChip({
  text,
  result,
  tone,
}: {
  text: string;
  result: number;
  tone: 'plain' | 'final' | 'solver';
}) {
  const skin =
    tone === 'final'
      ? 'border-good/45 bg-good/15 text-good'
      : tone === 'solver'
        ? 'border-blue-400/40 bg-blue-700/35 text-blue-200'
        : 'border-line bg-surface-2 text-ink';

  return (
    <div className={`flex items-center gap-2 rounded-tile border px-3 py-2 ${skin}`}>
      <span className="font-mono text-sm font-semibold">{text}</span>
      <span className="sr-only"> makes </span>
      <span aria-hidden="true" className="h-0.5 w-3 rounded-full bg-ink-dim" />
      <span className="tabular font-mono text-base font-bold">{result}</span>
    </div>
  );
}

export function Working({
  steps,
  tiles,
  tone,
}: {
  steps: NumbersStep[];
  tiles: number[];
  tone: 'plain' | 'solver';
}) {
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {steps.map((step, index) => (
        <StepChip
          key={step.id}
          text={stepText(step, tiles, steps)}
          result={step.result}
          tone={tone === 'solver' ? 'solver' : index === steps.length - 1 ? 'final' : 'plain'}
        />
      ))}
    </div>
  );
}

// --- The ruling ------------------------------------------------------------

// Nothing is ever drawn over an answer, and this is the component that exists
// because something once was. A NOT A WORD plate sat across the middle of the
// tiles, which meant the one moment a player most wants to read what they typed
// was the one moment they could not. The ruling is carried entirely by things
// that sit beside the word: the header says `claimed n` rather than `n letters`,
// the tiles lose the accent ring a good word gets, and this bar underneath names
// the ruling and the score.

export function RulingBar({
  good,
  label,
  score,
}: {
  good: boolean;
  label: string;
  score: number;
}) {
  const skin = good
    ? 'border-good/45 bg-good/15 text-good'
    : 'border-alert/45 bg-alert/15 text-alert';
  return (
    <div
      className={`flex shrink-0 items-center gap-3 rounded-card border px-4 py-3 transition-colors duration-300 ${skin}`}
    >
      <span className="font-semibold">{label}</span>
      <span className="tabular ml-auto text-2xl font-extrabold">{score}</span>
    </div>
  );
}
